import { useEffect, useMemo, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { createPortal } from "react-dom";
import classNames from "classnames";
import styles from "./style.module.scss";

const COLORS = {
  // Theme
  PRIMARY: "#2247c0",
  GREEN: "#099d42",
  BLUE_LIGHT: "#e5f3ff",
  RACE_BLUE: "#339dff",
  RED: "#c23b23",
  WHITE: "#ffffff",

  // Blueprint
  BLUEPRINT_LINE: "#4a9eff",
  BLUEPRINT_GLOW: "#6cb4ff",
  BLUEPRINT_GRID: "#1a3a6a",
  BLUEPRINT_DIM: "#2a6ab0",

  // Warm fills
  WALL_CREAM: "#f5e6d3",
  WALL_WARM: "#e8d5bf",
  ROOF_TERRACOTTA: "#c4725a",
  ROOF_SIDE: "#a85d48",
  DOOR_BROWN: "#8b6b4a",
  WINDOW_GLASS: "#a8d8ea",
  WINDOW_GLOW: "#ffd54f",
  FLOOR_WOOD: "#d4a574",
  FURNITURE: "#6d5c4a",
  GRASS: "#4a8b5c",
  GRASS_DARK: "#3a7048",
  PATH_STONE: "#c4b090",
  GROUND_LIGHT: "#a69070",

  // Effects
  SPARKLE_GOLD: "#ffd700",
  SPARKLE_WHITE: "#fffef0",
} as const;

const PHASE_DURATION_MS = 8000;
const OVERLAY_BG = "rgba(7, 7, 7, 0.92)";
const SPRING_SNAPPY = {
  type: "spring" as const,
  stiffness: 260,
  damping: 22,
};
const SPRING_BOUNCE = {
  type: "spring" as const,
  stiffness: 200,
  damping: 14,
};

type PhaseKey =
  | "Preliminary"
  | "Preliminary Finals"
  | "Final Review"
  | "Complete";

export interface PhaseTransitionAnimationProps {
  show: boolean;
  phaseLabel?: string | null;
  isFailure?: boolean;
  onComplete?: () => void;
}

function toPhaseKey(label?: string | null): PhaseKey | null {
  if (!label) return null;
  const lower = label.toLowerCase().replace(/[\s_-]/g, "");
  if (lower.includes("preliminary") && lower.includes("final"))
    return "Preliminary Finals";
  if (lower.includes("preliminary")) return "Preliminary";
  if (
    lower.includes("finalreview") ||
    (lower.includes("final") && !lower.includes("preliminary"))
  )
    return "Final Review";
  if (lower.includes("complete")) return "Complete";
  return null;
}

/* ═══════════════════════════════════════════════════════════════════════
   PHASE 1: PRELIMINARY — Blueprint wireframe with auto-sketch animation
   "We are designing the structure."
   ═══════════════════════════════════════════════════════════════════════ */

function PreliminaryAnimation() {
  const houseOutline = "M 60,155 L 60,85 L 140,35 L 220,85 L 220,155 Z";
  const door = "M 122,155 L 122,118 L 158,118 L 158,155";
  const windowLeft = "M 75,92 L 107,92 L 107,114 L 75,114 Z";
  const windowRight = "M 173,92 L 205,92 L 205,114 L 173,114 Z";
  const windowCrossLeft = "M 91,92 L 91,114 M 75,103 L 107,103";
  const windowCrossRight = "M 189,92 L 189,114 M 173,103 L 205,103";
  const chimney = "M 185,58 L 185,30 L 200,30 L 200,68";

  const dimBottom = "M 60,168 L 220,168";
  const dimLeft = "M 48,155 L 48,85";
  const dimArrows =
    "M 60,168 L 65,165 M 60,168 L 65,171 M 220,168 L 215,165 M 220,168 L 215,171";
  const dimArrowsV =
    "M 48,155 L 45,150 M 48,155 L 51,150 M 48,85 L 45,90 M 48,85 L 51,90";

  const gridH = [30, 55, 80, 105, 130, 155, 175];
  const gridV = [30, 55, 80, 105, 130, 155, 180, 205, 230, 250];

  return (
    <motion.div
      className="flex flex-col items-center justify-center"
      style={{ maxWidth: 400 }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <motion.svg
        width="280"
        height="195"
        viewBox="0 0 280 195"
        fill="none"
        className="mb-5"
        style={{ display: "block" }}
      >
        {/* Blueprint grid background */}
        {gridH.map((y, i) => (
          <motion.line
            key={`gh-${i}`}
            x1={20}
            y1={y}
            x2={260}
            y2={y}
            stroke={COLORS.BLUEPRINT_GRID}
            strokeWidth="0.5"
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.4 }}
            transition={{ duration: 0.6, delay: i * 0.04 }}
          />
        ))}
        {gridV.map((x, i) => (
          <motion.line
            key={`gv-${i}`}
            x1={x}
            y1={15}
            x2={x}
            y2={185}
            stroke={COLORS.BLUEPRINT_GRID}
            strokeWidth="0.5"
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.35 }}
            transition={{ duration: 0.6, delay: 0.2 + i * 0.03 }}
          />
        ))}

        {/* Subtle glow behind house */}
        <motion.ellipse
          cx="140"
          cy="105"
          rx="80"
          ry="60"
          fill={COLORS.BLUEPRINT_LINE}
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, 0.06, 0.03, 0.06] }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1,
          }}
        />

        {/* House outline — auto-sketch drawing animation */}
        <motion.path
          d={houseOutline}
          stroke={COLORS.BLUEPRINT_LINE}
          strokeWidth="2.5"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ pathLength: 1, opacity: 1 }}
          transition={{
            pathLength: {
              duration: 1.4,
              delay: 0.4,
              ease: [0.25, 0.46, 0.45, 0.94],
            },
            opacity: { duration: 0.2, delay: 0.4 },
          }}
        />

        {/* Glow duplicate of outline */}
        <motion.path
          d={houseOutline}
          stroke={COLORS.BLUEPRINT_GLOW}
          strokeWidth="6"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
          filter="url(#blueprintGlow)"
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ pathLength: 1, opacity: 0.3 }}
          transition={{
            pathLength: {
              duration: 1.4,
              delay: 0.4,
              ease: [0.25, 0.46, 0.45, 0.94],
            },
            opacity: { duration: 0.3, delay: 0.4 },
          }}
        />

        {/* Chimney */}
        <motion.path
          d={chimney}
          stroke={COLORS.BLUEPRINT_LINE}
          strokeWidth="1.5"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ pathLength: 1, opacity: 1 }}
          transition={{
            pathLength: { duration: 0.4, delay: 1.6, ease: "easeOut" },
            opacity: { duration: 0.15, delay: 1.6 },
          }}
        />

        {/* Door */}
        <motion.path
          d={door}
          stroke={COLORS.BLUEPRINT_LINE}
          strokeWidth="1.8"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ pathLength: 1, opacity: 1 }}
          transition={{
            pathLength: { duration: 0.5, delay: 1.8, ease: "easeOut" },
            opacity: { duration: 0.15, delay: 1.8 },
          }}
        />

        {/* Door handle */}
        <motion.circle
          cx="152"
          cy="137"
          r="2"
          fill={COLORS.BLUEPRINT_LINE}
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 2.2, duration: 0.2 }}
        />

        {/* Left window */}
        <motion.path
          d={windowLeft}
          stroke={COLORS.BLUEPRINT_LINE}
          strokeWidth="1.5"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ pathLength: 1, opacity: 1 }}
          transition={{
            pathLength: { duration: 0.4, delay: 2.0, ease: "easeOut" },
            opacity: { duration: 0.15, delay: 2.0 },
          }}
        />
        <motion.path
          d={windowCrossLeft}
          stroke={COLORS.BLUEPRINT_DIM}
          strokeWidth="0.8"
          fill="none"
          strokeLinecap="round"
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ pathLength: 1, opacity: 0.7 }}
          transition={{
            pathLength: { duration: 0.3, delay: 2.3, ease: "easeOut" },
            opacity: { duration: 0.15, delay: 2.3 },
          }}
        />

        {/* Right window */}
        <motion.path
          d={windowRight}
          stroke={COLORS.BLUEPRINT_LINE}
          strokeWidth="1.5"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ pathLength: 1, opacity: 1 }}
          transition={{
            pathLength: { duration: 0.4, delay: 2.1, ease: "easeOut" },
            opacity: { duration: 0.15, delay: 2.1 },
          }}
        />
        <motion.path
          d={windowCrossRight}
          stroke={COLORS.BLUEPRINT_DIM}
          strokeWidth="0.8"
          fill="none"
          strokeLinecap="round"
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ pathLength: 1, opacity: 0.7 }}
          transition={{
            pathLength: { duration: 0.3, delay: 2.4, ease: "easeOut" },
            opacity: { duration: 0.15, delay: 2.4 },
          }}
        />

        {/* Dimension lines — bottom */}
        <motion.path
          d={dimBottom}
          stroke={COLORS.BLUEPRINT_DIM}
          strokeWidth="0.8"
          fill="none"
          strokeDasharray="3 2"
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ pathLength: 1, opacity: 0.6 }}
          transition={{
            pathLength: { duration: 0.5, delay: 2.5 },
            opacity: { duration: 0.2, delay: 2.5 },
          }}
        />
        <motion.path
          d={dimArrows}
          stroke={COLORS.BLUEPRINT_DIM}
          strokeWidth="0.8"
          fill="none"
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.6 }}
          transition={{ duration: 0.2, delay: 2.9 }}
        />

        {/* Dimension lines — left */}
        <motion.path
          d={dimLeft}
          stroke={COLORS.BLUEPRINT_DIM}
          strokeWidth="0.8"
          fill="none"
          strokeDasharray="3 2"
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ pathLength: 1, opacity: 0.6 }}
          transition={{
            pathLength: { duration: 0.5, delay: 2.6 },
            opacity: { duration: 0.2, delay: 2.6 },
          }}
        />
        <motion.path
          d={dimArrowsV}
          stroke={COLORS.BLUEPRINT_DIM}
          strokeWidth="0.8"
          fill="none"
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.6 }}
          transition={{ duration: 0.2, delay: 3.0 }}
        />

        {/* Dimension text — bottom */}
        <motion.text
          x="140"
          y="180"
          textAnchor="middle"
          fill={COLORS.BLUEPRINT_DIM}
          fontSize="8"
          fontFamily="monospace"
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.7 }}
          transition={{ delay: 3.0, duration: 0.3 }}
        >
          {"24'-0\""}
        </motion.text>

        {/* Dimension text — left */}
        <motion.text
          x="38"
          y="125"
          textAnchor="middle"
          fill={COLORS.BLUEPRINT_DIM}
          fontSize="7"
          fontFamily="monospace"
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.7 }}
          transition={{ delay: 3.1, duration: 0.3 }}
        >
          {"12'-6\""}
        </motion.text>

        {/* Auto-sketch tracing cursor */}
        <motion.circle
          r="4"
          fill={COLORS.BLUEPRINT_GLOW}
          filter="url(#cursorGlow)"
          initial={{ opacity: 0, cx: 60, cy: 155 }}
          animate={{
            opacity: [0, 1, 1, 1, 1, 0],
            cx: [60, 60, 140, 220, 220, 220],
            cy: [155, 85, 35, 85, 155, 155],
          }}
          transition={{
            duration: 1.4,
            delay: 0.4,
            ease: "easeInOut",
          }}
        />

        {/* Pulsing glow on completed outline */}
        <motion.path
          d={houseOutline}
          stroke={COLORS.BLUEPRINT_GLOW}
          strokeWidth="4"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
          filter="url(#blueprintGlow)"
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, 0.15, 0.08, 0.15] }}
          transition={{
            duration: 2.5,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1.8,
          }}
        />

        {/* SVG Filters */}
        <defs>
          <filter
            id="blueprintGlow"
            x="-50%"
            y="-50%"
            width="200%"
            height="200%"
          >
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <filter id="cursorGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="4" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
      </motion.svg>

      {/* Phase title */}
      <motion.p
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6, ...SPRING_SNAPPY }}
        className={classNames(styles.phaseTitle, "text-center tracking-tight mt-0 mb-0")}
      >
        Preliminary
      </motion.p>
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: [0, 0.7, 1] }}
        transition={{ delay: 1.0, duration: 0.5 }}
        className={classNames(styles.phaseSubtitle, "text-center mt-2 mb-0")}
      >
        We are designing the structure
      </motion.p>
    </motion.div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   PHASE 2: PRELIMINARY FINALS — Wireframe fills with textures & details
   "Your vision is becoming realistic."
   ═══════════════════════════════════════════════════════════════════════ */

function PreliminaryFinalsAnimation() {
  const houseOutlineWire = "M 60,155 L 60,85 L 140,35 L 220,85 L 220,155 Z";
  const roofArea = "M 60,85 L 140,35 L 220,85 Z";
  const wallLeftArea = "M 60,85 L 60,155 L 140,155 L 140,85 Z";
  const wallRightArea = "M 140,85 L 140,155 L 220,155 L 220,85 Z";
  const doorArea = "M 122,118 L 158,118 L 158,155 L 122,155 Z";
  const windowLeftArea = "M 75,92 L 107,92 L 107,114 L 75,114 Z";
  const windowRightArea = "M 173,92 L 205,92 L 205,114 L 173,114 Z";

  // Furniture silhouettes
  const couch =
    "M 68,135 C 68,131 70,128 76,128 L 100,128 C 106,128 108,131 108,135 L 108,142 L 68,142 Z";
  const couchBack =
    "M 68,128 C 68,124 72,122 76,122 L 100,122 C 104,122 108,124 108,128";
  const tablePath =
    "M 176,133 L 204,133 L 204,136 L 176,136 Z M 179,136 L 179,142 M 201,136 L 201,142";
  const lampPath =
    "M 160,95 L 160,112 M 155,95 C 155,88 165,88 165,95 M 156,112 L 164,112";

  const floorLines = [
    "M 62,148 L 218,148",
    "M 62,151 L 218,151",
    "M 62,154 L 218,154",
  ];

  // Roof shingle lines — calculated inside the triangle
  const roofShingles = [50, 58, 66, 74].map((y) => {
    const t = (y - 35) / (85 - 35);
    const leftX = 140 - t * 80;
    const rightX = 140 + t * 80;
    return { y, leftX: leftX + 5, rightX: rightX - 5 };
  });

  return (
    <motion.div
      className="flex flex-col items-center justify-center"
      style={{ maxWidth: 400 }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <motion.svg
        width="280"
        height="175"
        viewBox="0 0 280 175"
        fill="none"
        className="mb-5"
        style={{ display: "block" }}
      >
        {/* Wireframe outline (fades as color fills in) */}
        <motion.path
          d={houseOutlineWire}
          stroke={COLORS.BLUEPRINT_LINE}
          strokeWidth="2"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
          initial={{ opacity: 0.7 }}
          animate={{ opacity: [0.7, 0.4, 0] }}
          transition={{ duration: 1.2, delay: 0.5, ease: "easeInOut" }}
        />

        {/* Wireframe details visible initially */}
        <motion.path
          d={`${doorArea} ${windowLeftArea} ${windowRightArea}`}
          stroke={COLORS.BLUEPRINT_LINE}
          strokeWidth="1.2"
          fill="none"
          initial={{ opacity: 0.5 }}
          animate={{ opacity: [0.5, 0.2, 0] }}
          transition={{ duration: 1.0, delay: 0.6, ease: "easeInOut" }}
        />

        {/* === COLOR FILL PHASE — bottom to top === */}

        {/* Floor wood pattern */}
        <motion.rect
          x="60"
          y="146"
          width="160"
          height="9"
          fill={COLORS.FLOOR_WOOD}
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.7 }}
          transition={{ duration: 0.5, delay: 0.6, ease: "easeOut" }}
        />
        {floorLines.map((d, i) => (
          <motion.path
            key={`floor-${i}`}
            d={d}
            stroke="#c49464"
            strokeWidth="0.6"
            fill="none"
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.5 }}
            transition={{ delay: 0.8 + i * 0.08, duration: 0.3 }}
          />
        ))}

        {/* Left wall fill */}
        <motion.path
          d={wallLeftArea}
          fill={COLORS.WALL_CREAM}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7, delay: 0.8, ease: "easeOut" }}
        />

        {/* Right wall fill (slightly darker for depth) */}
        <motion.path
          d={wallRightArea}
          fill={COLORS.WALL_WARM}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7, delay: 0.9, ease: "easeOut" }}
        />

        {/* Roof fill */}
        <motion.path
          d={roofArea}
          fill={COLORS.ROOF_TERRACOTTA}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 1.2, ease: "easeOut" }}
        />

        {/* Roof shingle detail lines */}
        {roofShingles.map((s, i) => (
          <motion.line
            key={`shingle-${i}`}
            x1={s.leftX}
            y1={s.y}
            x2={s.rightX}
            y2={s.y}
            stroke={COLORS.ROOF_SIDE}
            strokeWidth="0.6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.4 }}
            transition={{ delay: 1.4 + i * 0.08, duration: 0.3 }}
          />
        ))}

        {/* Door fill */}
        <motion.path
          d={doorArea}
          fill={COLORS.DOOR_BROWN}
          stroke="#7a5a3a"
          strokeWidth="1"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.3, duration: 0.4, ease: "easeOut" }}
        />
        {/* Door handle */}
        <motion.circle
          cx="153"
          cy="137"
          r="2.5"
          fill={COLORS.SPARKLE_GOLD}
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 1.65, ...SPRING_BOUNCE }}
        />

        {/* Left window glass */}
        <motion.path
          d={windowLeftArea}
          fill={COLORS.WINDOW_GLASS}
          stroke="#7ab8cc"
          strokeWidth="1.2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.9 }}
          transition={{ delay: 1.5, duration: 0.4, ease: "easeOut" }}
        />
        <motion.path
          d="M 91,92 L 91,114 M 75,103 L 107,103"
          stroke="#5a9aaa"
          strokeWidth="1"
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.6 }}
          transition={{ delay: 1.7, duration: 0.3 }}
        />

        {/* Right window glass */}
        <motion.path
          d={windowRightArea}
          fill={COLORS.WINDOW_GLASS}
          stroke="#7ab8cc"
          strokeWidth="1.2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.9 }}
          transition={{ delay: 1.6, duration: 0.4, ease: "easeOut" }}
        />
        <motion.path
          d="M 189,92 L 189,114 M 173,103 L 205,103"
          stroke="#5a9aaa"
          strokeWidth="1"
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.6 }}
          transition={{ delay: 1.8, duration: 0.3 }}
        />

        {/* Colored house outline (appears over fills) */}
        <motion.path
          d={houseOutlineWire}
          stroke="#9a7a5a"
          strokeWidth="2"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.1, duration: 0.5 }}
        />

        {/* === FURNITURE SILHOUETTES === */}

        {/* Couch (below left window) */}
        <motion.path
          d={couch}
          fill={COLORS.FURNITURE}
          initial={{ opacity: 0, y: 5 }}
          animate={{ opacity: 0.55, y: 0 }}
          transition={{ delay: 2.0, duration: 0.5, ease: "easeOut" }}
        />
        <motion.path
          d={couchBack}
          stroke={COLORS.FURNITURE}
          strokeWidth="1.5"
          fill="none"
          strokeLinecap="round"
          initial={{ opacity: 0, y: 5 }}
          animate={{ opacity: 0.55, y: 0 }}
          transition={{ delay: 2.05, duration: 0.5, ease: "easeOut" }}
        />

        {/* Table (below right window) */}
        <motion.path
          d={tablePath}
          stroke={COLORS.FURNITURE}
          strokeWidth="1.5"
          fill="none"
          strokeLinecap="round"
          initial={{ opacity: 0, y: 5 }}
          animate={{ opacity: 0.5, y: 0 }}
          transition={{ delay: 2.15, duration: 0.5, ease: "easeOut" }}
        />
        <motion.rect
          x="176"
          y="133"
          width="28"
          height="3"
          rx="0.5"
          fill={COLORS.FURNITURE}
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.4 }}
          transition={{ delay: 2.2, duration: 0.3 }}
        />

        {/* Lamp (on the right side) */}
        <motion.path
          d={lampPath}
          stroke={COLORS.FURNITURE}
          strokeWidth="1.2"
          fill="none"
          strokeLinecap="round"
          initial={{ opacity: 0, y: 3 }}
          animate={{ opacity: 0.45, y: 0 }}
          transition={{ delay: 2.25, duration: 0.4, ease: "easeOut" }}
        />

        {/* Ground / landscape */}
        <motion.line
          x1="30"
          y1="155"
          x2="250"
          y2="155"
          stroke={COLORS.GRASS_DARK}
          strokeWidth="2"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 0.5, delay: 1.8 }}
        />

        {/* Bushes */}
        <motion.ellipse
          cx="42"
          cy="152"
          rx="12"
          ry="6"
          fill={COLORS.GRASS}
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 0.7, scale: 1 }}
          transition={{ delay: 2.3, ...SPRING_BOUNCE }}
          style={{ transformOrigin: "42px 155px" }}
        />
        <motion.ellipse
          cx="238"
          cy="152"
          rx="10"
          ry="5"
          fill={COLORS.GRASS}
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 0.7, scale: 1 }}
          transition={{ delay: 2.4, ...SPRING_BOUNCE }}
          style={{ transformOrigin: "238px 155px" }}
        />

        {/* Render sweep line (moves bottom → top for "filling in" feel) */}
        <motion.rect
          x="55"
          y="0"
          width="170"
          height="8"
          fill="url(#sweepGradient)"
          initial={{ y: 158 }}
          animate={{ y: 30 }}
          transition={{
            duration: 1.0,
            delay: 0.5,
            ease: [0.22, 1, 0.36, 1],
          }}
        />

        <defs>
          <linearGradient id="sweepGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="white" stopOpacity="0" />
            <stop offset="50%" stopColor="white" stopOpacity="0.18" />
            <stop offset="100%" stopColor="white" stopOpacity="0" />
          </linearGradient>
        </defs>
      </motion.svg>

      {/* Phase title */}
      <motion.p
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5, ...SPRING_SNAPPY }}
        className={classNames(styles.phaseTitle, "text-center tracking-tight mt-0 mb-0")}
      >
        Preliminary Finals
      </motion.p>
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.9, duration: 0.4 }}
        className={classNames(styles.phaseSubtitle, "text-center mt-2 mb-0")}
      >
        Your vision is becoming realistic
      </motion.p>
    </motion.div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   PHASE 3: FINAL REVIEW — Fully rendered 3D home with lights & sparkles
   "This is your final designed home."
   ═══════════════════════════════════════════════════════════════════════ */

function FinalReviewAnimation() {
  // 3D isometric house geometry
  const frontWall = "M 50,105 L 170,105 L 170,170 L 50,170 Z";
  const rightWall = "M 170,105 L 228,78 L 228,143 L 170,170 Z";
  const frontRoof = "M 40,105 L 110,52 L 180,105 Z";
  const rightRoof = "M 180,105 L 110,52 L 168,25 L 238,78 Z";
  const roofRidge = "M 110,52 L 168,25";

  const frontDoor = "M 95,170 L 95,135 L 125,135 L 125,170";
  const doorStep = "M 90,170 L 130,170 L 130,174 L 90,174 Z";

  const frontWindowL = "M 58,118 L 82,118 L 82,140 L 58,140 Z";
  const frontWindowR = "M 138,118 L 158,118 L 158,140 L 138,140 Z";
  const sideWindow = "M 190,92 L 215,80 L 215,108 L 190,120 Z";

  const gardenPath = "M 100,174 L 95,190 L 120,190 L 115,174";

  // Light rays from front windows
  const lightRayL = "M 58,140 L 48,170 L 92,170 L 82,140";
  const lightRayR = "M 138,140 L 128,170 L 168,170 L 158,140";

  // Sparkle positions
  const sparkles = [
    { cx: 30, cy: 40, delay: 2.0, r: 2.5 },
    { cx: 248, cy: 55, delay: 2.2, r: 2 },
    { cx: 55, cy: 22, delay: 2.4, r: 1.8 },
    { cx: 210, cy: 28, delay: 2.1, r: 2.2 },
    { cx: 262, cy: 105, delay: 2.5, r: 1.5 },
    { cx: 18, cy: 88, delay: 2.3, r: 2 },
    { cx: 140, cy: 8, delay: 2.6, r: 2.5 },
    { cx: 78, cy: 12, delay: 2.35, r: 1.8 },
  ];

  return (
    <motion.div
      className="flex flex-col items-center justify-center"
      style={{ maxWidth: 400, overflow: "visible" }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      {/* Camera pan container */}
      <motion.div
        initial={{ x: -10, scale: 0.95 }}
        animate={{ x: 10, scale: 1 }}
        transition={{ duration: 3.5, ease: "easeInOut" }}
      >
        <motion.svg
          width="280"
          height="200"
          viewBox="0 0 280 200"
          fill="none"
          className="mb-5"
          style={{ display: "block", overflow: "visible" }}
          initial="hidden"
          animate="visible"
          variants={{
            hidden: {},
            visible: {
              transition: { staggerChildren: 0.05, delayChildren: 0.2 },
            },
          }}
        >
          {/* Ground shadow */}
          <motion.ellipse
            cx="140"
            cy="178"
            rx="110"
            ry="12"
            fill="#000"
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.15 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          />

          {/* Ground / grass */}
          <motion.rect
            x="15"
            y="170"
            width="250"
            height="25"
            rx="3"
            fill={COLORS.GRASS}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4, delay: 0.2 }}
          />
          <motion.rect
            x="15"
            y="170"
            width="250"
            height="4"
            fill={COLORS.GRASS_DARK}
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.6 }}
            transition={{ duration: 0.4, delay: 0.3 }}
          />

          {/* Right wall (side face, darker) */}
          <motion.path
            d={rightWall}
            fill={COLORS.WALL_WARM}
            stroke="#b8a48c"
            strokeWidth="1.5"
            strokeLinejoin="round"
            variants={{
              hidden: { opacity: 0, x: 15 },
              visible: {
                opacity: 1,
                x: 0,
                transition: { duration: 0.5, delay: 0.4, ease: "easeOut" },
              },
            }}
          />

          {/* Front wall */}
          <motion.path
            d={frontWall}
            fill={COLORS.WALL_CREAM}
            stroke="#c8b8a0"
            strokeWidth="1.5"
            strokeLinejoin="round"
            variants={{
              hidden: { opacity: 0, x: -15 },
              visible: {
                opacity: 1,
                x: 0,
                transition: { duration: 0.5, delay: 0.3, ease: "easeOut" },
              },
            }}
          />

          {/* Front roof */}
          <motion.path
            d={frontRoof}
            fill={COLORS.ROOF_TERRACOTTA}
            stroke="#a85a48"
            strokeWidth="1.5"
            strokeLinejoin="round"
            variants={{
              hidden: { opacity: 0, y: -15 },
              visible: {
                opacity: 1,
                y: 0,
                transition: { duration: 0.5, delay: 0.6, ease: "easeOut" },
              },
            }}
          />

          {/* Right roof */}
          <motion.path
            d={rightRoof}
            fill={COLORS.ROOF_SIDE}
            stroke="#8a4a38"
            strokeWidth="1.5"
            strokeLinejoin="round"
            variants={{
              hidden: { opacity: 0, y: -15 },
              visible: {
                opacity: 1,
                y: 0,
                transition: { duration: 0.5, delay: 0.65, ease: "easeOut" },
              },
            }}
          />

          {/* Roof ridge line */}
          <motion.path
            d={roofRidge}
            stroke="#8a4a38"
            strokeWidth="2"
            strokeLinecap="round"
            variants={{
              hidden: { opacity: 0 },
              visible: {
                opacity: 1,
                transition: { delay: 0.8, duration: 0.3 },
              },
            }}
          />

          {/* Chimney */}
          <motion.g
            variants={{
              hidden: { opacity: 0, y: -8 },
              visible: {
                opacity: 1,
                y: 0,
                transition: { delay: 0.85, duration: 0.3 },
              },
            }}
          >
            <rect
              x="155"
              y="32"
              width="14"
              height="24"
              rx="1"
              fill="#9a6a50"
              stroke="#8a5a40"
              strokeWidth="1"
            />
            <rect x="153" y="30" width="18" height="4" rx="1" fill="#8a5a40" />
          </motion.g>

          {/* Chimney smoke wisps */}
          {[0, 1, 2].map((i) => (
            <motion.circle
              key={`smoke-${i}`}
              cx={162 - i * 3}
              cy={26}
              r={3 + i}
              fill="#aaa"
              initial={{ opacity: 0, y: 0 }}
              animate={{
                opacity: [0, 0.15, 0.1, 0],
                y: [0, -8 - i * 6, -16 - i * 8, -24 - i * 10],
                x: [0, -2 - i, -4 - i * 2, -6 - i * 3],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                delay: 1.2 + i * 0.5,
                ease: "easeOut",
              }}
            />
          ))}

          {/* Door */}
          <motion.path
            d={frontDoor}
            fill={COLORS.DOOR_BROWN}
            stroke="#6a5040"
            strokeWidth="1.2"
            strokeLinecap="round"
            strokeLinejoin="round"
            variants={{
              hidden: { opacity: 0, scaleY: 0 },
              visible: {
                opacity: 1,
                scaleY: 1,
                transition: { delay: 0.9, ...SPRING_SNAPPY },
              },
            }}
            style={{ transformOrigin: "110px 170px" }}
          />

          {/* Door step */}
          <motion.path
            d={doorStep}
            fill={COLORS.PATH_STONE}
            variants={{
              hidden: { opacity: 0 },
              visible: {
                opacity: 1,
                transition: { delay: 1.0, duration: 0.2 },
              },
            }}
          />

          {/* Door handle */}
          <motion.circle
            cx="121"
            cy="153"
            r="2"
            fill={COLORS.SPARKLE_GOLD}
            variants={{
              hidden: { opacity: 0, scale: 0 },
              visible: {
                opacity: 1,
                scale: 1,
                transition: { delay: 1.1, ...SPRING_BOUNCE },
              },
            }}
          />

          {/* Front left window */}
          <motion.path
            d={frontWindowL}
            fill={COLORS.WINDOW_GLASS}
            stroke="#7ab8cc"
            strokeWidth="1.2"
            variants={{
              hidden: { opacity: 0, scale: 0.8 },
              visible: {
                opacity: 1,
                scale: 1,
                transition: { delay: 1.0, ...SPRING_SNAPPY },
              },
            }}
            style={{ transformOrigin: "70px 129px" }}
          />
          <motion.path
            d="M 70,118 L 70,140 M 58,129 L 82,129"
            stroke="#5a9aaa"
            strokeWidth="0.8"
            variants={{
              hidden: { opacity: 0 },
              visible: {
                opacity: 0.6,
                transition: { delay: 1.15, duration: 0.2 },
              },
            }}
          />

          {/* Front right window */}
          <motion.path
            d={frontWindowR}
            fill={COLORS.WINDOW_GLASS}
            stroke="#7ab8cc"
            strokeWidth="1.2"
            variants={{
              hidden: { opacity: 0, scale: 0.8 },
              visible: {
                opacity: 1,
                scale: 1,
                transition: { delay: 1.05, ...SPRING_SNAPPY },
              },
            }}
            style={{ transformOrigin: "148px 129px" }}
          />
          <motion.path
            d="M 148,118 L 148,140 M 138,129 L 158,129"
            stroke="#5a9aaa"
            strokeWidth="0.8"
            variants={{
              hidden: { opacity: 0 },
              visible: {
                opacity: 0.6,
                transition: { delay: 1.2, duration: 0.2 },
              },
            }}
          />

          {/* Side window */}
          <motion.path
            d={sideWindow}
            fill={COLORS.WINDOW_GLASS}
            stroke="#7ab8cc"
            strokeWidth="1.2"
            variants={{
              hidden: { opacity: 0, scale: 0.8 },
              visible: {
                opacity: 1,
                scale: 1,
                transition: { delay: 1.1, ...SPRING_SNAPPY },
              },
            }}
            style={{ transformOrigin: "202px 100px" }}
          />

          {/* === LIGHTS TURNING ON in windows === */}
          <motion.path
            d={frontWindowL}
            fill={COLORS.WINDOW_GLOW}
            filter="url(#windowGlow)"
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 0, 0.7, 0.5, 0.7] }}
            transition={{ duration: 1.5, delay: 1.5, ease: "easeInOut" }}
          />
          <motion.path
            d={frontWindowR}
            fill={COLORS.WINDOW_GLOW}
            filter="url(#windowGlow)"
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 0, 0.6, 0.4, 0.6] }}
            transition={{ duration: 1.5, delay: 1.7, ease: "easeInOut" }}
          />
          <motion.path
            d={sideWindow}
            fill={COLORS.WINDOW_GLOW}
            filter="url(#windowGlow)"
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 0, 0.5, 0.3, 0.5] }}
            transition={{ duration: 1.5, delay: 1.9, ease: "easeInOut" }}
          />

          {/* Light rays spilling from windows onto ground */}
          <motion.path
            d={lightRayL}
            fill={COLORS.WINDOW_GLOW}
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 0.08, 0.04, 0.08] }}
            transition={{
              duration: 2,
              delay: 1.8,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
          <motion.path
            d={lightRayR}
            fill={COLORS.WINDOW_GLOW}
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 0.06, 0.03, 0.06] }}
            transition={{
              duration: 2,
              delay: 2.0,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />

          {/* Garden walkway */}
          <motion.path
            d={gardenPath}
            fill={COLORS.PATH_STONE}
            stroke={COLORS.GROUND_LIGHT}
            strokeWidth="0.5"
            variants={{
              hidden: { opacity: 0 },
              visible: {
                opacity: 0.8,
                transition: { delay: 1.3, duration: 0.3 },
              },
            }}
          />

          {/* Bushes / landscaping */}
          <motion.g
            variants={{
              hidden: { opacity: 0, scale: 0 },
              visible: {
                opacity: 1,
                scale: 1,
                transition: { delay: 1.4, ...SPRING_BOUNCE },
              },
            }}
            style={{ transformOrigin: "35px 170px" }}
          >
            <ellipse cx="35" cy="164" rx="14" ry="10" fill={COLORS.GRASS} />
            <ellipse cx="35" cy="160" rx="10" ry="8" fill="#5a9b6c" />
          </motion.g>
          <motion.g
            variants={{
              hidden: { opacity: 0, scale: 0 },
              visible: {
                opacity: 1,
                scale: 1,
                transition: { delay: 1.5, ...SPRING_BOUNCE },
              },
            }}
            style={{ transformOrigin: "245px 170px" }}
          >
            <ellipse cx="245" cy="166" rx="10" ry="7" fill={COLORS.GRASS} />
            <ellipse cx="245" cy="163" rx="7" ry="5" fill="#5a9b6c" />
          </motion.g>

          {/* === SPARKLE PARTICLES === */}
          {sparkles.map((s, i) => (
            <motion.g key={`sparkle-${i}`}>
              <motion.circle
                cx={s.cx}
                cy={s.cy}
                r={s.r}
                fill={COLORS.SPARKLE_GOLD}
                filter="url(#sparkleGlow)"
                initial={{ opacity: 0, scale: 0 }}
                animate={{
                  opacity: [0, 1, 0.6, 1, 0],
                  scale: [0, 1.2, 0.8, 1, 0],
                }}
                transition={{
                  duration: 1.5,
                  delay: s.delay,
                  ease: "easeInOut",
                }}
              />
              {/* Sparkle cross (+ shape) */}
              <motion.path
                d={`M ${s.cx - 4},${s.cy} L ${s.cx + 4},${s.cy} M ${s.cx},${s.cy - 4} L ${s.cx},${s.cy + 4}`}
                stroke={COLORS.SPARKLE_WHITE}
                strokeWidth="0.8"
                strokeLinecap="round"
                initial={{ opacity: 0, scale: 0 }}
                animate={{
                  opacity: [0, 0.8, 0.4, 0.8, 0],
                  scale: [0, 1.3, 0.9, 1.1, 0],
                }}
                transition={{
                  duration: 1.5,
                  delay: s.delay + 0.05,
                  ease: "easeInOut",
                }}
                style={{
                  transformOrigin: `${s.cx}px ${s.cy}px`,
                }}
              />
            </motion.g>
          ))}

          {/* Final polish shine sweep */}
          <motion.rect
            x="0"
            y="0"
            width="40"
            height="200"
            fill="url(#shineSweep)"
            initial={{ x: -40 }}
            animate={{ x: 300 }}
            transition={{
              duration: 1.0,
              delay: 2.5,
              ease: [0.22, 1, 0.36, 1],
            }}
          />

          {/* SVG Filters & Gradients */}
          <defs>
            <filter
              id="windowGlow"
              x="-50%"
              y="-50%"
              width="200%"
              height="200%"
            >
              <feGaussianBlur stdDeviation="4" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
            <filter
              id="sparkleGlow"
              x="-100%"
              y="-100%"
              width="300%"
              height="300%"
            >
              <feGaussianBlur stdDeviation="2" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
            <linearGradient id="shineSweep" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="white" stopOpacity="0" />
              <stop offset="50%" stopColor="white" stopOpacity="0.12" />
              <stop offset="100%" stopColor="white" stopOpacity="0" />
            </linearGradient>
          </defs>
        </motion.svg>
      </motion.div>

      {/* Phase title */}
      <motion.p
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5, ...SPRING_SNAPPY }}
        className={classNames(styles.phaseTitle, "text-center tracking-tight mt-0 mb-0")}
      >
        Final Review
      </motion.p>
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.9, duration: 0.4 }}
        className={classNames(styles.phaseSubtitle, "text-center mt-2 mb-0")}
      >
        This is your final designed home
      </motion.p>
    </motion.div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   COMPLETE — Green checkmark animation (unchanged)
   ═══════════════════════════════════════════════════════════════════════ */

function CompleteAnimation() {
  return (
    <motion.div
      initial={{ scale: 0.5, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      className="flex flex-col items-center justify-center text-white"
    >
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.15, ...SPRING_BOUNCE }}
        className="w-16 h-16 rounded-full border-4 flex items-center justify-center"
        style={{ borderColor: COLORS.GREEN }}
      >
        <span className="text-3xl" style={{ color: COLORS.GREEN }}>
          &#x2713;
        </span>
      </motion.div>
      <motion.p
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.35, duration: 0.35 }}
        className={classNames(styles.phaseCompleteTitle, "text-center mt-4 mb-0")}
      >
        Project complete
      </motion.p>
    </motion.div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   FAILURE — Red X animation (unchanged)
   ═══════════════════════════════════════════════════════════════════════ */

function FailureAnimation() {
  return (
    <motion.div
      initial={{ scale: 0.5, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      className="flex flex-col items-center justify-center text-white"
    >
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.15, ...SPRING_BOUNCE }}
        className="w-16 h-16 rounded-full border-4 flex items-center justify-center"
        style={{ borderColor: COLORS.RED }}
      >
        <motion.span
          className="text-3xl font-bold"
          style={{ color: COLORS.RED }}
          initial={{ opacity: 0, rotate: -90 }}
          animate={{ opacity: 1, rotate: 0 }}
          transition={{ delay: 0.3, ...SPRING_SNAPPY }}
        >
          &times;
        </motion.span>
      </motion.div>
      <motion.p
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.35, duration: 0.35 }}
        className={classNames(styles.phaseCompleteTitle, "text-center mt-4 mb-0")}
      >
        Something went wrong
      </motion.p>
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5, duration: 0.35 }}
        className={classNames(styles.phaseFailureSubtitle, "text-center mt-1.5 mb-0")}
      >
        We couldn&apos;t move to the next phase. Please try again.
      </motion.p>
    </motion.div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   SPARK DUST INTRO — Premium white particle burst on phase transition
   ═══════════════════════════════════════════════════════════════════════ */

function SparkDustIntro() {
  const particles = useMemo(() => {
    return Array.from({ length: 36 }, (_, i) => {
      const angle = (i / 36) * Math.PI * 2 + (Math.random() - 0.5) * 0.5;
      const distance = 180 + Math.random() * 420;
      const endX = Math.cos(angle) * distance;
      const endY = Math.sin(angle) * distance - 24;
      const size = 1.5 + Math.random() * 2;
      const delay = Math.random() * 0.2;
      const duration = 0.7 + Math.random() * 0.5;
      return { endX, endY, size, delay, duration };
    });
  }, []);

  return (
    <motion.div
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        pointerEvents: "none",
        zIndex: 2,
      }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      {/* Central bloom */}
      <motion.div
        style={{
          position: "absolute",
          width: 80,
          height: 80,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(255,255,255,0.12) 0%, transparent 70%)",
        }}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: [0, 3, 5], opacity: [0, 0.3, 0] }}
        transition={{ duration: 0.8, ease: "easeOut" }}
      />

      {/* Spark particles */}
      {particles.map((p, i) => (
        <motion.div
          key={i}
          style={{
            position: "absolute",
            width: p.size * 2,
            height: p.size * 2,
            borderRadius: "50%",
            background: "white",
            boxShadow: `0 0 ${p.size * 3}px ${p.size}px rgba(255, 255, 255, 0.3)`,
          }}
          initial={{ x: 0, y: 0, opacity: 0, scale: 0 }}
          animate={{
            x: p.endX,
            y: p.endY,
            opacity: [0, 0.85, 0.6, 0],
            scale: [0, 1.3, 1, 0.2],
          }}
          transition={{
            duration: p.duration,
            delay: p.delay,
            ease: "easeOut",
          }}
        />
      ))}
    </motion.div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   PHASE CONTENT ROUTER
   ═══════════════════════════════════════════════════════════════════════ */

function PhaseContent({ phaseKey }: { phaseKey: PhaseKey }) {
  switch (phaseKey) {
    case "Preliminary":
      return <PreliminaryAnimation />;
    case "Preliminary Finals":
      return <PreliminaryFinalsAnimation />;
    case "Final Review":
      return <FinalReviewAnimation />;
    case "Complete":
      return <CompleteAnimation />;
    default:
      return <CompleteAnimation />;
  }
}

/* ═══════════════════════════════════════════════════════════════════════
   MAIN OVERLAY COMPONENT
   ═══════════════════════════════════════════════════════════════════════ */

const OVERLAY_Z_INDEX = 1000000;
const FAILURE_DURATION_MS = 2500;

export function PhaseTransitionAnimation({
  show,
  phaseLabel,
  isFailure = false,
  onComplete,
}: PhaseTransitionAnimationProps) {
  const phaseKey = toPhaseKey(phaseLabel);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const durationMs = isFailure ? FAILURE_DURATION_MS : PHASE_DURATION_MS;

  useEffect(() => {
    if (!show) return;
    const t = setTimeout(() => {
      onCompleteRef.current?.();
    }, durationMs);
    return () => clearTimeout(t);
  }, [show, durationMs]);

  const overlay = (
    <AnimatePresence>
      {show && (
        <motion.div
          key="phase-overlay"
          style={{
            zIndex: OVERLAY_Z_INDEX,
            position: "fixed",
            inset: 0,
            minHeight: "100vh",
            minWidth: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "auto",
          }}
          initial={{ opacity: 0, background: "rgba(7, 7, 7, 0)" }}
          animate={{
            opacity: 1,
            background: isFailure
              ? OVERLAY_BG
              : [
                  "rgba(7, 7, 7, 0)",
                  "rgba(7, 7, 7, 0.45)",
                  "rgba(7, 7, 7, 0.92)",
                ],
          }}
          exit={{ opacity: 0 }}
          transition={{
            opacity: { duration: isFailure ? 0.25 : 0.5 },
            background: { duration: isFailure ? 0.25 : 0.7, ease: "easeOut" },
          }}
        >
          {/* White spark dust on phase transitions (not on failure) */}
          {!isFailure && <SparkDustIntro />}

          {/* Main animation content */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{
              duration: 0.3,
              delay: isFailure ? 0 : 0.35,
            }}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {isFailure ? (
              <FailureAnimation />
            ) : phaseKey ? (
              <PhaseContent phaseKey={phaseKey} />
            ) : (
              <CompleteAnimation />
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return typeof document !== "undefined"
    ? createPortal(overlay, document.body)
    : overlay;
}

export function PhaseSuccessAnimation({ show }: { show: boolean }) {
  return (
    <PhaseTransitionAnimation
      show={show}
      phaseLabel={null}
      onComplete={undefined}
    />
  );
}
